import { useQuery } from "@tanstack/react-query";
import { queryClient } from "app/global";
import { routes } from "pages/routes";
import type { SignInLocationState } from "pages/SignIn/SignIn";
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { bootIntercom } from "services/intercom";
import {
  getUserAgent,
  logError,
  logInfo,
  updateUserLoggingContext,
} from "services/logging";
import { identifyUser } from "services/productAnalytics";
import { fetchStudentProfile } from "services/student";
import { currentUserQuery } from "services/users";
import { clearUserSession, logOut } from "services/users/auth";
import { User } from "services/users/model";
import { setTagAudience } from "services/tagManager";
import { EnhancedUser, enhanceUser } from "services/users/utils";
import { SessionStorageKeys } from "utils/storage";
import { useRequiredContext } from "./utils";

export type UserContextValue = {
  // undefined = session status unknown, null = no session
  user: User | null | undefined;
  refreshCurrentUser: () => Promise<User>;
  refetchStudent: () => void;
  signOut: (signInState?: SignInLocationState | null) => Promise<void>;
};

export const UserContext = createContext<UserContextValue | undefined>(undefined);
UserContext.displayName = "UserContext";

export function UserContextProvider({ children }: PropsWithChildren) {
  const { data: user } = useQuery(currentUserQuery());

  useEffect(() => {
    setTagAudience(user);
    if (!user) {
      return;
    }

    sessionStorage.setItem(SessionStorageKeys.user, JSON.stringify(user));
    identifyUser(user);
    updateUserState(user);
    updateUserLoggingContext(user);

    logInfo("signed in, browser: " + getUserAgent());

    void bootIntercom(user).catch(logError);
    return () => void bootIntercom(null).catch(logError);
  }, [user]);

  const refetchStudent = useCallback(async () => {
    try {
      const student = await fetchStudentProfile();
      updateUserState(usr => ({
        ...usr,
        student,
      }));
    } catch (error) {
      logError("Failed to refetch the student profile", error);
    }
  }, []);

  const navigate = useNavigate();
  const signOut = useCallback(
    async (signInState?: SignInLocationState | null) => {
      try {
        const resp = await logOut();
        if (resp?.logout_url) {
          window.location.replace(resp.logout_url);
        } else {
          navigate(routes.public.signIn.path(), { state: signInState });
        }
      } finally {
        clearUserSession();
      }
    },
    [navigate]
  );

  const contextValue = useMemo(
    () => ({
      user,
      refreshCurrentUser,
      refetchStudent,
      signOut,
    }),
    [user, refetchStudent, signOut]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export function useUser(required?: true): EnhancedUser;
export function useUser(required: boolean): EnhancedUser | null | undefined;
export function useUser(required = true) {
  const { user } = useRequiredContext(UserContext);

  if (required && !user) {
    throw new Error("Required user data is not available.");
  }

  return useMemo(() => (user ? enhanceUser(user) : user), [user]);
}

async function refreshCurrentUser() {
  void queryClient.invalidateQueries(currentUserQuery());
  const response = await queryClient.fetchQuery(currentUserQuery());
  if (!response) {
    throw new Error("User session has expired");
  }
  return response;
}

export function updateUserState(newData: User | ((currentUser: User) => User)) {
  const existingUser = queryClient.getQueryData(currentUserQuery().queryKey);
  if (!existingUser) {
    throw new Error("No user data available to update.");
  }
  const updatedUserData = typeof newData === "function" ? newData(existingUser) : newData;
  queryClient.setQueryData(currentUserQuery().queryKey, updatedUserData);
  sessionStorage.setItem(SessionStorageKeys.user, JSON.stringify(updatedUserData));
}
